import React, { useEffect, useState } from 'react';
import { X, Pin, Star, FileText, Image as ImageIcon } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@easyflow/ui-frontend/components/ui/avatar';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { cn } from '@/lib/utils';
import { messengerApi } from '../lib/messengerApi';
import { formatTime, getAvatarGradient, getInitials } from '../utils/format';
import { PresenceDot } from './PresenceDot';
import type { ChatMessage, ConversationSummary, MessageAttachment, ParticipantBrief, PresenceStatus } from '../types';

interface Props {
  conversation: ConversationSummary;
  participants: ParticipantBrief[];
  presenceMap: Record<string, PresenceStatus>;
  onClose: () => void;
}

interface SharedFile extends MessageAttachment {
  sender: string;
  date: string;
}

function parseAttachments(att: ChatMessage['attachments']): MessageAttachment[] {
  if (!att) return [];
  if (Array.isArray(att)) return att;
  if (typeof att === 'string') {
    try { const p = JSON.parse(att); return Array.isArray(p) ? p : []; } catch { return []; }
  }
  return [];
}

// Task #1660 — Info panel desktop (lg:flex) con 4 tab: Membri / Fissati /
// Preferiti / Media. Mantiene la parità con il monolite originale.
export function InfoPanel(props: Props) {
  const { conversation, participants, presenceMap, onClose } = props;
  const [tab, setTab] = useState<'members' | 'pinned' | 'favorites' | 'media'>('members');
  const [pinned, setPinned] = useState<ChatMessage[]>([]);
  const [favorites, setFavorites] = useState<ChatMessage[]>([]);
  const [shared, setShared] = useState<SharedFile[]>([]);

  useEffect(() => {
    if (tab === 'pinned') {
      void messengerApi.getPinned(conversation.id).then((res) => {
        if (res.success && res.data) setPinned(res.data);
      });
    } else if (tab === 'favorites') {
      void messengerApi.getFavorites().then((res) => {
        if (res.success && res.data) setFavorites(res.data);
      });
    } else if (tab === 'media') {
      void messengerApi.getMessages(conversation.id, undefined, 100).then((res) => {
        if (!res.success || !res.data) return;
        const files: SharedFile[] = [];
        for (const m of res.data) {
          for (const a of parseAttachments(m.attachments)) {
            files.push({
              ...a,
              sender: `${m.sender_first_name || ''} ${m.sender_last_name || ''}`.trim(),
              date: m.created_at,
            });
          }
        }
        setShared(files);
      });
    }
  }, [tab, conversation.id]);

  return (
    <div className="w-80 border-l bg-white dark:bg-gray-900 hidden lg:flex flex-col">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-semibold text-sm">Dettagli</h3>
        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-4 text-center border-b">
        <Avatar className="h-16 w-16 mx-auto ring-2 ring-white dark:ring-gray-800 shadow-md">
          <AvatarImage src={conversation.avatar_url || conversation.other_participants?.[0]?.avatar_url || undefined} />
          <AvatarFallback className={`bg-gradient-to-br ${getAvatarGradient(conversation.display_title || '')} text-white text-xl font-bold`}>
            {conversation.type === 'channel' ? '#' : getInitials(conversation.display_title?.split(' ')[0], conversation.display_title?.split(' ')[1])}
          </AvatarFallback>
        </Avatar>
        <h4 className="font-semibold mt-3">{conversation.display_title}</h4>
        <p className="text-xs text-muted-foreground mt-0.5">
          {conversation.type === 'direct' ? 'Chat diretta' : `${conversation.participant_count || 0} membri`}
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)} className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="w-[calc(100%-2rem)] h-auto p-1 mx-4 mt-2 rounded-lg bg-gray-100 dark:bg-gray-800">
          <TabsTrigger value="members" className="text-[10px] flex-1 rounded-md">Membri</TabsTrigger>
          <TabsTrigger value="pinned" className="text-[10px] flex-1 rounded-md">Fissati</TabsTrigger>
          <TabsTrigger value="favorites" className="text-[10px] flex-1 rounded-md">Preferiti</TabsTrigger>
          <TabsTrigger value="media" className="text-[10px] flex-1 rounded-md">Media</TabsTrigger>
        </TabsList>

        <ScrollArea className="flex-1 p-3">
          <TabsContent value="members" className="mt-0">
            {participants.map((p) => {
              const name = `${p.first_name || ''} ${p.last_name || ''}`.trim();
              const status = presenceMap[p.id] || null;
              return (
                <div key={p.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  <div className="relative">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={p.avatar_url || undefined} />
                      <AvatarFallback className={`bg-gradient-to-br ${getAvatarGradient(name)} text-white text-[10px] font-bold`}>
                        {getInitials(p.first_name, p.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <PresenceDot status={status} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium truncate block">{name}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {status === 'online' ? 'Online'
                        : status === 'away' ? 'Assente'
                        : status === 'in-call' ? 'In chiamata'
                        : status === 'dnd' ? 'Non disturbare'
                        : 'Offline'}
                    </span>
                  </div>
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="pinned" className="mt-0">
            {pinned.length === 0 && (
              <div className="text-center py-8">
                <Pin className="h-8 w-8 mx-auto text-gray-200 dark:text-gray-700 mb-2" />
                <p className="text-xs text-muted-foreground">Nessun messaggio fissato</p>
              </div>
            )}
            {pinned.map((m) => (
              <div key={m.id} className="p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 mb-2 border border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-1">
                  <Pin className="h-3 w-3 text-amber-500 rotate-45" />
                  <span className="text-xs font-semibold">{m.sender_first_name} {m.sender_last_name}</span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-3">{m.content}</p>
                <span className="text-[10px] text-muted-foreground">{formatTime(m.created_at)}</span>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="favorites" className="mt-0">
            {favorites.length === 0 && (
              <div className="text-center py-8">
                <Star className="h-8 w-8 mx-auto text-gray-200 dark:text-gray-700 mb-2" />
                <p className="text-xs text-muted-foreground">Nessun messaggio preferito</p>
              </div>
            )}
            {favorites.map((m) => (
              <div key={m.id} className="p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 mb-2 border border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-1">
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                  <span className="text-xs font-semibold">{m.sender_first_name} {m.sender_last_name}</span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-3">{m.content}</p>
                <span className="text-[10px] text-muted-foreground">{formatTime(m.created_at)}</span>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="media" className="mt-0">
            {shared.length === 0 && (
              <div className="text-center py-8">
                <FileText className="h-8 w-8 mx-auto text-gray-200 dark:text-gray-700 mb-2" />
                <p className="text-xs text-muted-foreground">Nessun file condiviso</p>
              </div>
            )}
            {shared.map((f, i) => (
              <div key={`${f.filename}-${i}`} className={cn('flex items-center gap-2.5 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors')}>
                <div className="h-8 w-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
                  {f.mime_type?.startsWith('image/')
                    ? <ImageIcon className="h-4 w-4 text-blue-500" />
                    : <FileText className="h-4 w-4 text-gray-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{f.filename}</p>
                  <p className="text-[10px] text-muted-foreground">{f.sender} · {formatTime(f.date)}</p>
                </div>
              </div>
            ))}
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </div>
  );
}
